<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import { addMinutes, differenceInSeconds } from 'date-fns'
import formatDate from 'date-fns/format'
import VueBarcode from 'vue-barcode'
import QrcodeVue from 'qrcode.vue'
import moment from 'moment'
import userHasPermission from '@/utils/user-has-permission'
// import Tooltip from '@/components/Tooltip'
import order from '@/store/modules/order'
import { ellipsisEnd, decimalCount } from '@/utils/index.js'
import { countryData } from '@/constants/country-data'
import { validEmail } from '@/utils/validate'
import { PAY_NOW } from '@/constants/payment-types'
import { formatCurrency, formatBarcodeValue } from '@/utils/format'
import Amount from '@/components/Amount/Amount.vue'
const ORDER_TYPE_QR_CODE = 'qr-code'
const ORDER_TYPE_PAYMENT_LINK = 'payment-link'
const ORDER_TYPE_STORAGE_KEY = 'preferred-order-type'
export default {
  name: 'CreateOrderDrawer',
  components: {
    // Tooltip,
    VueBarcode,
    QrcodeVue,
    Amount,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    close: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      minutesTillCheckoutExpires: 15,
      isAdvance: false,
      visibleDialog: false,
      ORDER_TYPE_QR_CODE,
      ORDER_TYPE_PAYMENT_LINK,
      timerSeconds: 0,
      timerInterval: null,
      qrCodeLink: '',
      orderType: ORDER_TYPE_QR_CODE,
      allowTypeSelection: false,
      loading: false,
      creatingOrder: false,
      inStoreOrderCreationTime: null,
      createdOrderResponse: {},
      counter: 0,
      intervalId: null,
      storeSelected: '',
      cacheExpiryTimeInMinutes: '', // will be used when resending the link with expiry time
      dialogs: {
        cancelConfirmationDialog: false,
        loadingDialog: false,
        smsSentDialog: false,
        successDialog: false,
        failedDialog: false,
        qrDialog: false,
        errorDialog: false,
      },
      error: {
        title: '',
        message: '',
      },
      linkExpired: false,
      ellipsisEnd,
      decimalCount,
      orderCreationForm: {
        phoneNumber: '',
        store: '',
        locale: 'ar_SA',
        email: '',
        orderReferenceId: '',
        orderReferenceIdQR: '',
        amount: '',
        paymentType: '',
        expiryTime: '',
      },
      locales: [
        {
          name: 'Arabic / العربية',
          value: 'ar_SA',
          label: 'ar_SA',
        },
        {
          name: 'English / الإنجليزية',
          value: 'en_US',
          label: 'en_US',
        },
      ],
      defaultTime: new Date(new Date().setHours(0, 5, 0, 0)),
    }
  },
  computed: {
    ...mapGetters('translations', ['currentLocale']),
    ...mapState('order', [
      'merchantBranches',
      'paymentPlans',
      'creatingInStoreOrder',
      'fetchingDetail',
    ]),
    ...mapGetters('order', ['getOrderDetail']),
    ...mapState('barcodeSettings', ['settings']),
    ...mapGetters('profile', ['profile', 'inStoreDirectIntegrationEnabled']),
    ...mapGetters('userProfile', ['selectedMerchant']),
    rules() {
      return {
        checkEmpty: [
          {
            required: true,
            message: this.$t('general_validation_empty'),
            trigger: 'blur',
          },
        ],
        store: [
          {
            required: true,
            message: this.$t('general_validation_empty'),
            trigger: 'blur',
          },
        ],
        phoneNumber: [
          {
            required: this.orderType === ORDER_TYPE_PAYMENT_LINK,
            message: this.$t('general_validation_empty'),
            trigger: 'blur',
          },
          { validator: this.checkPhone, trigger: 'blur' },
        ],
        email: [
          {
            required: false,
            message: this.$t('general_validation_empty'),
            trigger: 'blur',
          },
          { validator: this.checkEmail, trigger: 'blur' },
        ],
        expiryTime: [{ validator: this.checkExpiryTime, trigger: 'blur' }],
        amount: [
          {
            required: true,
            message: this.$t('general_validation_empty'),
            trigger: 'blur',
          },
          { validator: this.checkAmount, trigger: 'blur' },
        ],
      }
    },
    isDisabledPaymentLinkSubmit() {
      return (
        this.loading ||
        this.isDisabledSubmitLink ||
        this?.$refs?.orderCreationForm?.fields[6]?.validateState === 'error'
      )
    },
    activeBranches() {
      if (!this.merchantBranches) {
        return []
      }
      return this.merchantBranches.filter(
        (branch) => branch.branchStatus === 'active'
      )
    },
    isDisabledSubmitQR() {
      return (
        !this.orderCreationForm.amount ||
        (this.merchantHasActiveBranches && !this.orderCreationForm.store)
      )
    },
    isDisabledSubmitLink() {
      return (
        !this.orderCreationForm.amount ||
        (this.merchantHasActiveBranches && !this.orderCreationForm.store) ||
        !this.orderCreationForm.phoneNumber
      )
    },

    order() {
      return order
    },
    isDialogShowing() {
      return Object.values(this.dialogs).some((value) => value === true)
    },
    timer() {
      const minutes = Math.floor(this.timerSeconds / 60)
      const seconds = this.timerSeconds % 60
      if (minutes === 0 && seconds === 0) {
        return this.$t('qr_code_expired_label')
      }
      return `${minutes}:${seconds}`
    },
    userCanCreateInStoreOrder() {
      return (
        this.profile &&
        this.profile.inStore &&
        userHasPermission(['order_create'])
      )
    },
    merchantHasActiveBranches() {
      return (
        this.activeBranches &&
        Array.isArray(this.activeBranches) &&
        this.activeBranches.length !== 0
      )
    },
    countryDecimal() {
      return this.merchantCountryData?.countryCode?.toLowerCase() === 'kw'
        ? '0.000'
        : '0.00'
    },
    orderDetail() {
      return this.getOrderDetail(this.createdOrderResponse.orderId)
    },
    merchantCountryData() {
      if (!this.profile || !this.profile.country) {
        return {}
      }
      return countryData.find(
        (item) =>
          item.countryCode.toLowerCase() === this.profile.country.toLowerCase()
      )
    },
    futureTime() {
      let futureTime = addMinutes(this.inStoreOrderCreationTime, 15)
      if (this.orderCreationForm.expiryTime) {
        futureTime = addMinutes(
          this.inStoreOrderCreationTime,
          this.getExpiryTimeInMinutes(this.orderCreationForm.expiryTime)
        )
      }
      return futureTime
    },
    linkExpireTimer() {
      if (!this.inStoreOrderCreationTime) {
        return
      }

      const dateFormat = 'dd/MM/yy, HH:mm a'
      return formatDate(this.futureTime, dateFormat)
    },
    barcodeValue() {
      if (
        !this.orderDetail ||
        Object.keys(this.orderDetail).length === 0 ||
        this.orderDetail.status !== 'fully_captured'
      ) {
        return
      }
      let barcodeValue = ''
      if (
        this.settings['barcode.approvalCode'] &&
        this.settings['barcode.amount']
      ) {
        barcodeValue = `${this.orderDetail.orderReferenceId};${this.orderDetail.totalAmount.amount} ${this.orderDetail.totalAmount.currency}`
      } else if (this.settings['barcode.approvalCode']) {
        barcodeValue = `${this.orderDetail.orderReferenceId}`
      } else if (this.settings['barcode.amount']) {
        barcodeValue = `${this.orderDetail.totalAmount.amount} ${this.orderDetail.totalAmount.currency}`
      }
      return barcodeValue
    },
    barcodeValueWillBeDisplayed() {
      return formatBarcodeValue(this.barcodeValue)
    },
    isPaymentPlanDisabled() {
      return (
        !(this.orderCreationForm.amount && this.orderCreationForm.amount > 0) ||
        this.paymentPlans.length === 0
      )
    },
    mapPaymentPlans() {
      if (this.paymentPlans && this.paymentPlans.length > 0) {
        const paymentPlans = this.paymentPlans.map((item) => {
          return {
            paymentType: item.name,
          }
        })
        return paymentPlans
      }
      return []
    },
    isArabic() {
      return this.currentLocale === 'ar_SA'
    },
    isOnlyPayNowAvailable() {
      return (
        this.paymentPlans.length === 1 && this.paymentPlans[0].name === PAY_NOW
      )
    },
    amountPlaceholder() {
      const formattedCurrency = formatCurrency(
        this.merchantCountryData.currency
      )

      return `${formattedCurrency} ${this.countryDecimal}`
    },
    merchantCountryCode() {
      return this.merchantCountryData?.countryCode?.toLowerCase()
    },
  },
  watch: {
    orderDetail: function(newValue, oldValue) {
      if (!newValue || Object.keys(newValue).length === 0) {
        return
      }

      if (newValue.status === 'declined') {
        clearInterval(this.intervalId)
        this.showDialog('failedDialog')
      }

      if (newValue.status === 'fully_captured') {
        clearInterval(this.intervalId)
        this.showDialog('successDialog')
      }
    },
    counter: function(newValue, oldValue) {
      if (newValue <= 0) {
        clearInterval(this.intervalId)
        this.linkExpired = true
      }
    },
    visible: function(newValue) {
      this.visibleDialog = newValue
      this.fetchBranches()
      this.getBarcodeSetting({ merchantId: this.profile.merchantId })
      this.setPreferredTypeSelection()
      window.addEventListener('beforeunload', this.reloadHandler)
      this.orderCreationForm.locale =
        this.merchantCountryData?.countryCode?.toLowerCase() === 'sa'
          ? 'ar_SA'
          : 'en_US'
    },
    activeBranches: function(newValue) {
      if (newValue.length && !this.orderCreationForm.store) {
        this.orderCreationForm.store = newValue[0]?.name
      }
      // Set default branch
    },
    orderType: function(newValue) {
      if (this.activeBranches.length && !this.orderCreationForm.store) {
        this.orderCreationForm.store = this.activeBranches[0]?.name
      }
      // Set default branch when order type change
    },
  },
  mounted() {
    if (this.paymentPlans.length === 1) {
      this.orderCreationForm.paymentType = this.paymentPlans[0].name
    }
  },
  beforeDestroy() {
    clearInterval(this.intervalId)
    window.removeEventListener('beforeunload', this.reloadHandler)
  },
  methods: {
    ...mapGetters('translations', ['currentDirection']),
    ...mapActions('order', [
      'createInStoreOrder',
      'createInStoreOrderQr',
      'cancelInStoreOrder',
      'fetchOrderDetail',
      'fetchPaymentPlans',
    ]),
    ...mapActions('barcodeSettings', ['getBarcodeSetting']),
    ...mapActions('order', ['fetchMerchantBranches', 'fetchPaymentPlans']),
    handleBackToEdit() {
      this.hideAllDialogs()
    },
    toggleAdvance() {
      this.isAdvance = !this.isAdvance
    },
    handleClose() {
      // this.resetForm('orderCreationForm')
      // this.resetForm('orderCreationFormQR')
      this.visibleDialog = false
      this.isAdvance = false
      this.close()
      if (this.dialogs.successDialog || this.dialogs.failedDialog) {
        this.hideAllDialogs()
        this.counter = 0
      }
    },
    customPaymentTypeFormatter(paymentType) {
      const key = this.$options.filters.paymentTypeFormatter(paymentType)

      // Split in 3 is different only in this component, it should show up as Pay By Installments
      if (key === 'payment_type_instalment') {
        return 'order_create_pay_by_installments'
      }
      return key
    },
    async fetchBranches() {
      const merchantIds = [this.selectedMerchant]
      await this.fetchMerchantBranches(merchantIds)
    },
    backToList() {
      this.$router.push({
        path: `/order/list`,
      })
    },
    setPreferredTypeSelection() {
      // Check if both solutions are enabled
      if (
        this.profile.inStoreQrCodeSolutionEnabled &&
        this.profile.inStorePaymentLinkEnabled
      ) {
        const preferred = window.localStorage.getItem(ORDER_TYPE_STORAGE_KEY)
        if (preferred) {
          this.orderType = preferred
        }
        this.allowTypeSelection = true
      } else if (this.profile.inStoreQrCodeSolutionEnabled) {
        // Only QR code is enabled
        this.allowTypeSelection = false
        this.orderType = ORDER_TYPE_QR_CODE
      } else if (this.profile.inStorePaymentLinkEnabled) {
        // Only payment link is enabled
        this.allowTypeSelection = false
        this.orderType = ORDER_TYPE_PAYMENT_LINK
      }
    },
    updatePreferredType(orderType) {
      window.localStorage.setItem(ORDER_TYPE_STORAGE_KEY, orderType)
      this.orderType = orderType
      this.resetForm('orderCreationForm')
      this.resetForm('orderCreationFormQR')
    },
    // fetch order details to see if it has been maximum of 180 times (re-fetch for every 5 seconds)
    startExpireLinkTimer() {
      this.startVisualTimer()
      const now = new Date()
      // Calculate the number of iterations based on the expiry time or set it to a maximum of 180
      this.counter = this.futureTime
        ? Math.round(differenceInSeconds(this.futureTime, now) / 5)
        : 180
      // Set an interval to decrement the counter and check conditions to stop the timer
      this.intervalId = setInterval(() => {
        // Decrement the counter
        this.counter--
        // Check if the counter reaches 0 or if the order detail is fully captured
        if (
          this.counter <= 0 ||
          (this.orderDetail &&
            Object.keys(this.orderDetail).length !== 0 &&
            this.orderDetail.status === 'fully_captured')
        ) {
          // Stop the interval if the conditions are met
          clearInterval(this.intervalId)
        }

        if (this.orderDetail.status === 'expired') {
          clearInterval(this.intervalId)
          this.linkExpired = true

          return
        }

        // Call the getOrder() method after each iteration
        this.getOrder()
      }, 5000)
    },
    startVisualTimer() {
      // Setting 15 minutes timer;
      this.timerSeconds = 15 * 60
      // Set an interval to decrement the timer value
      this.timerInterval = setInterval(() => {
        // Decrement the timer value by 1 second
        this.timerSeconds--
        // If the timer reaches 0, clear the interval
        if (this.timerSeconds === 0) {
          clearInterval(this.timerInterval)
        }
      }, 1000)
    },
    async getOrder() {
      if (!this.fetchingDetail) {
        const isPingToFetchOrderDetail = 1

        await this.fetchOrderDetail({
          orderId: this.createdOrderResponse.orderId,
          merchantId: this.profile.merchantId,
          ping: isPingToFetchOrderDetail,
        })
      }
    },
    reloadHandler(event) {
      if (this.visibleDialog) {
        // Cancel the event as stated by the standard.
        event.preventDefault()
        // Chrome requires returnValue to be set.
        event.returnValue = ''
      }
    },
    resetForm(formName) {
      if (this.$refs[formName]) {
        this.$refs[formName].resetFields()
      }
      this.orderCreationForm = {
        phoneNumber: '',
        store: '',
        locale: 'ar_SA',
        email: '',
        orderReferenceId: '',
        orderReferenceIdQR: '',
        amount: '',
        paymentType: '',
        expiryTime: '',
      }
    },
    async showDialog(dialogName) {
      await this.hideAllDialogs()
      this.dialogs[dialogName] = true
    },
    hideAllDialogs() {
      Object.keys(this.dialogs).forEach((dialog, index) => {
        this.dialogs[dialog] = false
      })
    },
    checkPhone(rule, value, callback) {
      if (this.orderType !== ORDER_TYPE_PAYMENT_LINK) {
        callback()
        return
      }
      if (!value) {
        callback(new Error(this.$t('general_empty_field_message')))
      }
      const isNum = /^\d+$/.test(value)
      if (!isNum) {
        callback(new Error(this.$t('general_numbers_only_field_message')))
      }
      if (value.length < this.merchantCountryData.numberMinLength) {
        callback(
          new Error(
            `"${this.merchantCountryData.countryName}" ${this.$t(
              'general_phone_validation_text1'
            )} ${this.merchantCountryData.numberMinLength} ${this.$t(
              'general_phone_validation_text2'
            )}`
          )
        )
      }
      if (value.length > this.merchantCountryData.numberMaxLength) {
        callback(
          new Error(
            `"${this.merchantCountryData.countryName}" ${this.$t(
              'general_phone_validation_text3'
            )} ${this.merchantCountryData.numberMaxLength} ${this.$t(
              'general_phone_validation_text2'
            )}`
          )
        )
      }
      const firstNumber = Number(value.substring(0, 1))
      if (!this.merchantCountryData.numberStartsWith.includes(firstNumber)) {
        const message =
          this.currentLocale === 'ar_SA'
            ? `يرجي ادخال رقم خاتف خاص بـ "${this.merchantCountryData.countryName}" صحيح `
            : `Please enter a valid "${this.merchantCountryData.countryName}" phone number`
        callback(new Error(message))
      }

      callback()
    },
    checkEmail(rule, value, callback) {
      if (value === '') {
        callback()
        return
      }
      if (!validEmail(value)) {
        callback(new Error(this.$t('general_email_validation_text1')))
        return
      }
      callback()
    },
    checkExpiryTime(rule, value, callback) {
      if (!value) {
        callback()
        return
      }
      if (this.getExpiryTimeInMinutes(value) < 5) {
        callback(new Error(this.$t('expiry_time_error_minimum')))
      }
      callback()
    },
    checkAmount(rule, value, callback) {
      if (!value) {
        callback(new Error(this.$t('general_empty_field_message')))
      }
      if (decimalCount(value) > this.merchantCountryData.decimalDigits) {
        callback(
          new Error(
            `${this.$t('general_amount_validation_text1')} ${
              this.merchantCountryData.decimalDigits
            } ${this.$t('general_amount_validation_text2')}`
          )
        )
      }

      callback()
    },
    async submitQrForm(formName) {
      const that = this

      if (this.paymentPlans.length === 0) {
        await this.onChangeAmount(this.orderCreationForm.amount)
      }

      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          const orderData = {
            totalAmount: {
              amount: this.orderCreationForm.amount,
              currency: this.merchantCountryData.currency,
            },
            orderReferenceId: this.orderCreationForm.orderReferenceIdQR,
            additionalData: { store_code: this.orderCreationForm.store },
            locale: this.orderCreationForm.locale,
            paymentType: this.orderCreationForm.paymentType,
            paymentPlans: this.paymentPlans,
          }

          try {
            that.showDialog('loadingDialog')
            const response = await that.createInStoreOrderQr(orderData)

            this.inStoreOrderCreationTime = new Date()
            this.createdOrderResponse = response

            this.qrCodeLink = this.createdOrderResponse.checkoutDeeplink
            // fetch order details till it is fully captured to show the success dialog
            this.linkExpired = false
            this.startExpireLinkTimer()
            this.showDialog('qrDialog')

            this.startExpireLinkTimer()

            return true
          } catch (err) {
            this.dialogs.loadingDialog = false
            this.showErrorMessage(err)
            return false
          }
        } else {
          return false
        }
      })
    },
    async submitPaymentLinkForm(formName, isResendLink) {
      const that = this

      if (this.paymentPlans.length === 0) {
        await this.onChangeAmount(this.orderCreationForm.amount)
      }

      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          const expiryTimeInMinutes = this.getExpiryTimeInMinutes(
            this.orderCreationForm.expiryTime,
            isResendLink
          )

          const orderData = {
            totalAmount: {
              amount: this.orderCreationForm.amount,
              currency: this.merchantCountryData.currency,
            },
            phoneNumber:
              this.merchantCountryData.telephoneCode +
              this.orderCreationForm.phoneNumber,
            orderReferenceId: this.orderCreationForm.orderReferenceId,
            email: this.orderCreationForm.email,
            additionalData: { store_code: this.orderCreationForm.store },
            locale: this.orderCreationForm.locale,
            paymentType: this.orderCreationForm.paymentType,
            expiryTime: expiryTimeInMinutes,
            paymentPlans: this.paymentPlans,
          }

          try {
            that.showDialog('loadingDialog')
            const response = await that.createInStoreOrder(orderData)
            this.inStoreOrderCreationTime = new Date()
            this.createdOrderResponse = response
            this.linkExpired = false
            this.startExpireLinkTimer()

            that.showDialog('smsSentDialog')
            return true
          } catch (err) {
            that.hideAllDialogs()
            this.showErrorMessage(err)
          }
        } else {
          return false
        }
      })
    },
    getExpiryTimeInMinutes(datetime, isResendLink) {
      if (!datetime) {
        return ''
      }

      if (!isResendLink) {
        const calculatedExpiryTime =
          moment(datetime).hours() * 60 + moment(datetime).minutes()
        this.cacheExpiryTimeInMinutes = calculatedExpiryTime
        return calculatedExpiryTime
      } else {
        this.orderCreationForm.expiryTime = moment()
          .add(this.cacheExpiryTimeInMinutes, 'minutes')
          .toDate()
        return this.cacheExpiryTimeInMinutes
      }
    },
    showErrorMessage(err) {
      if (typeof err === 'string' && (err === 'cancel' || err === 'close')) {
        return
      }

      const response = err.response || {}
      const { data } = response
      const errorCode =
        data && data.errors && data.errors.length > 0
          ? data.errors[data.errors.length - 1]?.error_code
          : ''

      let translationKey
      if (errorCode === 'payment_type_unsupported') {
        translationKey = 'error_order_creation_payment_type_unsupported'
      } else {
        translationKey = errorCode
      }

      const title = this.$te(`${translationKey}_title`)
        ? this.$t(`${translationKey}_title`)
        : this.$t('general_error_title')

      const message = this.$te(`${translationKey}_subtitle`)
        ? this.$t(`${translationKey}_subtitle`)
        : (data && data.message) || this.$t('general_error_message')
      this.error = {
        title,
        message,
      }
      this.showDialog('errorDialog')
    },
    filterMerchantById(id) {
      return this.subMerchants.filter((obj) => {
        return obj.merchantId === id
      })
    },
    calcMerchantName(merchant) {
      if (this.currentLocale === 'ar_SA') {
        return merchant.arMerchantName || merchant.merchantName
      }
      return merchant.merchantName || merchant.arMerchantName
    },
    cancelOrderClicked() {
      this.showDialog('cancelConfirmationDialog')
    },
    async cancelOrderProcess() {
      this.showDialog('loadingDialog')
      this.resetForm('orderCreationForm')
      this.resetForm('orderCreationFormQR')

      const data = {
        checkoutId: this.createdOrderResponse.checkoutId,
        orderId: this.createdOrderResponse.checkoutId,
      }
      await this.cancelInStoreOrder(data)
      this.hideAllDialogs()
      this.counter = 0
    },
    goToOrderDetails() {
      this.closeLastStepDialog()
      setTimeout(() => {
        this.$router.push({
          path: `/order/detail/${this.createdOrderResponse.orderId}?merchantId=${this.profile.merchantId}`,
        })
      }, 500)
    },
    stayOnPage() {
      this.orderType === ORDER_TYPE_QR_CODE
        ? this.showDialog('qrDialog')
        : this.showDialog('smsSentDialog')
    },
    closeLastStepDialog() {
      this.handleClose()
      this.hideAllDialogs()
      this.counter = 0
    },
    async onChangeAmount(value) {
      if (value > 0) {
        await this.fetchPaymentPlans({
          countryCode: this.merchantCountryData.countryCode,
          amount: value,
          currency: this.merchantCountryData.currency,
        })

        this.orderCreationForm.paymentType = ''
        if (this.paymentPlans.length === 1) {
          this.orderCreationForm.paymentType = this.paymentPlans[0].name
        }
      }
    },
    handleSubmitCreateOrder(type) {
      if (type === ORDER_TYPE_QR_CODE) {
        this.submitQrForm('orderCreationFormQR')
      } else {
        this.submitPaymentLinkForm('orderCreationForm', false)
      }
    },
  },
}
</script>

<template>
  <div
    :class="
      (isAdvance || inStoreDirectIntegrationEnabled) &&
      !isDialogShowing &&
      orderType === ORDER_TYPE_PAYMENT_LINK
        ? 'create-order create-order-advance'
        : 'create-order'
    "
  >
    <el-drawer
      :direction="currentLocale === 'ar_SA' ? 'ltr' : 'rtl'"
      :with-header="false"
      :modal-append-to-body="false"
      custom-class="create-order-drawer"
      :before-close="handleClose"
      :visible="
        visibleDialog &&
          userCanCreateInStoreOrder &&
          (profile.inStoreQrCodeSolutionEnabled ||
            profile.inStorePaymentLinkEnabled)
      "
    >
      <div class="create-order__title">
        {{ $t('create_order_drawer_title') }}
        <span @click="handleClose">
          <i class="el-icon-close" />
        </span>
      </div>
      <div
        v-if="!isDialogShowing && allowTypeSelection"
        class="create-order__btn-group"
      >
        <el-button
          :type="orderType === ORDER_TYPE_QR_CODE ? 'primary' : ''"
          :class="
            `${
              orderType === ORDER_TYPE_QR_CODE
                ? 'create-order__button'
                : 'create-order__button-secondary'
            }`
          "
          @click="() => updatePreferredType(ORDER_TYPE_QR_CODE)"
        >
          <svg-icon
            :icon-class="
              orderType === ORDER_TYPE_QR_CODE ? 'qr-order-active' : 'qr-order'
            "
          />

          {{ $t('create_order_type_qr_code') }}
        </el-button>
        <el-button
          :type="orderType === ORDER_TYPE_PAYMENT_LINK ? 'primary' : ''"
          :class="
            `${
              orderType === ORDER_TYPE_PAYMENT_LINK
                ? 'create-order__button'
                : 'create-order__button-secondary'
            }`
          "
          @click="() => updatePreferredType(ORDER_TYPE_PAYMENT_LINK)"
        >
          <svg-icon
            :icon-class="
              orderType === ORDER_TYPE_PAYMENT_LINK
                ? 'payment-link-active'
                : 'payment-link'
            "
          />

          {{ $t('create_order_type_payment_link') }}
        </el-button>
      </div>
      <div v-if="!isDialogShowing" class="create-order__advance">
        {{ $t('create_order_drawer_switch') }}
        <el-switch
          v-model="isAdvance"
          class="create-order__switch"
          @click="toggleAdvance"
        />
      </div>

      <el-form
        v-show="orderType === ORDER_TYPE_QR_CODE && !isDialogShowing"
        ref="orderCreationFormQR"
        :model="orderCreationForm"
        :disabled="creatingOrder || creatingInStoreOrder"
      >
        <el-form-item
          :label="$t('order_create_order_amount')"
          prop="amount"
          :rules="rules.amount"
        >
          <el-input
            v-model="orderCreationForm.amount"
            class="value-field global__font-currency-primary"
            :placeholder="amountPlaceholder"
            @change="onChangeAmount"
          />
        </el-form-item>
        <el-form-item
          :label="$t('order_create_receipt_language')"
          prop="locale"
          :rules="rules.checkEmpty"
          class="value-field"
        >
          <el-select
            v-model="orderCreationForm.locale"
            placeholder="Select"
            popper-class="global__select global__select--new-ui"
            class="w-full"
          >
            <el-option
              v-for="item in locales"
              :key="item.value"
              :label="item.name"
              :value="item.value"
            >
              <span class="flex items-center justify-between align-middle">
                <span>
                  {{ item.name }}
                </span>
                <svg-icon
                  v-if="item.value === orderCreationForm.locale"
                  icon-class="check"
                />
              </span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item
          v-if="merchantHasActiveBranches"
          :label="$t('order_create__store')"
          prop="store"
          :rules="rules.checkEmpty"
          class="value-field"
        >
          <el-select
            v-model="orderCreationForm.store"
            style="width: 100%"
            size="large"
            popper-class="global__select global__select--new-ui global__select--new-ui-multiple"
            :placeholder="$t('order_list_select_store')"
            filterable
            clearable
          >
            <el-option
              v-for="(item, index) in activeBranches"
              :key="index"
              :label="ellipsisEnd(item.name, 40)"
              :value="item.name"
            >
              <span>
                <span>
                  <el-tooltip
                    placement="right"
                    :open-delay="200"
                    :content="item.name"
                  >
                    <div>
                      <div>
                        <div>
                          {{ ellipsisEnd(item.name, 40) }}
                        </div>
                      </div>
                      <div class="global__select-check-icon">
                        <svg-icon
                          v-if="item.name === storeSelected"
                          icon-class="check"
                        />
                      </div>
                    </div>
                  </el-tooltip>
                </span>
              </span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item
          v-if="isAdvance"
          prop="orderReferenceIdQR"
          class="value-field"
        >
          <span slot="label">
            {{ $t('order_create_order_reference') }}
            <span>{{ $t('order_create_optional') }}</span>
          </span>
          <el-input
            v-model="orderCreationForm.orderReferenceIdQR"
            :placeholder="$t('order_create_order_reference_placeholder')"
            class="value-field"
          />
        </el-form-item>
        <div class="create-order__btn-group-footer">
          <el-button
            class="create-order__button-secondary footer-btn"
            :loading="creatingOrder || creatingInStoreOrder"
            :disabled="loading"
            @click="handleClose"
          >
            {{ $t('text_cancel') }}
          </el-button>
          <el-button
            :type="!isDisabledSubmitQR ? 'primary' : ''"
            class="create-order__button footer-btn"
            :loading="creatingOrder || creatingInStoreOrder"
            :disabled="loading || isDisabledSubmitQR"
            @click="handleSubmitCreateOrder(ORDER_TYPE_QR_CODE)"
          >
            {{ $t('order_create_place_an_order') }}
          </el-button>
        </div>
      </el-form>

      <el-form
        v-show="orderType === ORDER_TYPE_PAYMENT_LINK && !isDialogShowing"
        ref="orderCreationForm"
        :model="orderCreationForm"
        :disabled="creatingOrder"
        :class="isAdvance ? 'create-order-advance' : ''"
      >
        <el-form-item
          :label="$t('order_create_order_amount')"
          prop="amount"
          :rules="rules.amount"
        >
          <el-input
            v-model="orderCreationForm.amount"
            :placeholder="amountPlaceholder"
            class="value-field global__font-currency-primary"
            @change="onChangeAmount"
          />
        </el-form-item>
        <el-form-item
          :label="$t('order_create_customer_phone_number')"
          prop="phoneNumber"
          :rules="rules.phoneNumber"
        >
          <el-input
            v-model="orderCreationForm.phoneNumber"
            :placeholder="$t('order_create_phone_number')"
            class="value-field create-order__phone-field"
          >
            <template :slot="isArabic ? 'append' : 'prepend'">
              <span class="create-order__phone-prefix">
                <div>
                  <svg-icon
                    class="create-order__phone-prefix-flag"
                    :icon-class="merchantCountryCode"
                  />
                </div>
                <div class="global__always-ltr">
                  +{{ merchantCountryData.telephoneCode }}
                </div>
                <div>
                  <svg-icon
                    class="create-order__phone-prefix-arrow"
                    icon-class="point-down"
                  />
                </div>
              </span>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item
          v-if="merchantHasActiveBranches"
          :label="$t('order_create__store')"
          prop="store"
          :rules="rules.checkEmpty"
          class="value-field"
        >
          <el-select
            v-model="orderCreationForm.store"
            style="width: 100%"
            size="large"
            popper-class="global__select global__select--new-ui global__select--new-ui-multiple"
            :placeholder="$t('order_list_select_store')"
            filterable
            clearable
          >
            <el-option
              v-for="(item, index) in activeBranches"
              :key="index"
              :label="ellipsisEnd(item.name, 40)"
              :value="item.name"
            >
              <span>
                <span>
                  <el-tooltip
                    placement="right"
                    :open-delay="200"
                    :content="item.name"
                  >
                    <div>
                      <div>
                        <div>
                          {{ ellipsisEnd(item.name, 40) }}
                        </div>
                      </div>
                      <div class="global__select-check-icon">
                        <svg-icon
                          v-if="item.name === storeSelected"
                          icon-class="check"
                        />
                      </div>
                    </div>
                  </el-tooltip>
                </span>
              </span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item
          :label="$t('order_create_receipt_language')"
          prop="locale"
          :rules="rules.checkEmpty"
          class="value-field"
        >
          <el-select
            v-model="orderCreationForm.locale"
            placeholder="Select"
            popper-class="global__select global__select--new-ui"
            class="w-full"
          >
            <el-option
              v-for="item in locales"
              :key="item.value"
              :label="item.name"
              :value="item.value"
            >
              <span class="flex items-center justify-between align-middle">
                <span>
                  {{ item.name }}
                </span>
                <svg-icon
                  v-if="item.value === orderCreationForm.locale"
                  icon-class="check"
                />
              </span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item
          v-if="inStoreDirectIntegrationEnabled || isAdvance"
          prop="orderReferenceId"
          :rules="inStoreDirectIntegrationEnabled ? rules.checkEmpty : {}"
        >
          <span slot="label">
            {{ $t('order_create_order_reference') }}
            <span v-if="!inStoreDirectIntegrationEnabled">
              {{ $t('order_create_optional') }}
            </span>
          </span>
          <el-input
            v-model="orderCreationForm.orderReferenceId"
            :placeholder="$t('order_create_order_reference_placeholder')"
            class="value-field"
          />
        </el-form-item>

        <el-form-item v-if="isAdvance" prop="email" :rules="rules.email">
          <span slot="label">
            {{ $t('order_create_email') }}
            <span>{{ $t('order_create_optional') }}</span>
          </span>
          <el-input
            v-model="orderCreationForm.email"
            :placeholder="'name@example.com'"
            class="value-field"
          />
        </el-form-item>

        <el-form-item
          v-if="isAdvance"
          prop="expiryTime"
          class="value-field"
          :rules="rules.expiryTime"
        >
          <span slot="label">
            {{ $t('order_create_expiry_time') }}
            <span>{{ $t('order_create_optional') }}</span>
          </span>
          <el-time-picker
            v-model="orderCreationForm.expiryTime"
            style="width: 100%"
            size="large"
            :format="'HH : mm'"
            :disabled-hours="[0]"
            :disabled-minutes="[0, 1, 2, 3, 4, 5]"
            :default-value="defaultTime"
            :teleported="false"
            popper-class="create-order-advance__time-picker-popper"
            placeholder="hh:mm"
          />
        </el-form-item>

        <el-form-item
          v-if="isOnlyPayNowAvailable"
          :label="$t('order_create_payment_plan')"
          prop="paymentType"
          :rules="rules.checkEmpty"
        >
          <el-select
            v-model="orderCreationForm.paymentType"
            :placeholder="$t('order_create_payment_plan_placeholder')"
            popper-class="global__select global__select--new-ui"
            class="w-full"
            :disabled="isPaymentPlanDisabled"
          >
            <el-option
              v-for="item in mapPaymentPlans"
              :key="item.paymentType"
              :label="
                customPaymentTypeFormatter(item.paymentType || 'N/A')
                  | translatePaymentType(3)
              "
              :value="item.paymentType"
            >
              <span class="flex items-center justify-between align-middle">
                <span>
                  {{
                    customPaymentTypeFormatter(item.paymentType || 'N/A')
                      | translatePaymentType(3)
                  }}
                </span>
              </span>
            </el-option>
          </el-select>
        </el-form-item>
        <div class="create-order__btn-group-footer">
          <el-button
            class="create-order__button-secondary footer-btn"
            :loading="creatingOrder"
            :disabled="loading"
            @click="handleClose"
          >
            {{ $t('text_cancel') }}
          </el-button>
          <el-button
            :type="!isDisabledSubmitLink ? 'primary' : ''"
            class="create-order__button footer-btn"
            :loading="creatingOrder"
            :disabled="isDisabledPaymentLinkSubmit"
            @click="handleSubmitCreateOrder(ORDER_TYPE_PAYMENT_LINK)"
          >
            {{ $t('order_create_place_an_order') }}
          </el-button>
        </div>
      </el-form>

      <!-- <div> -->
      <div v-if="dialogs['qrDialog']">
        <h2 class="create-order__dialog__heading--qr">
          <!-- {{ $t('qr_dialog_heading_normal') }}
          {{ $t('qr_dialog_heading_bold') }} -->
          Scan using Tamara app
        </h2>
        <div class="qr-wrapper">
          <QrcodeVue
            :value="qrCodeLink"
            class-name="qr-code__wrap"
            :size="200"
          />
        </div>

        <div class="create-order__dialog__timer">
          <span v-if="!linkExpired">
            {{ $t('qr_code_expiry_label') }}
          </span>
          {{ timer }}
        </div>
        <div class="create-order__divider" />
        <div class="create-order__dialog__amount">
          <div class="create-order__dialog__amount__label">
            {{ $t('order_create_wait_total') }}
          </div>
          <div class="create-order__dialog__amount__total">
            <Amount
              align="center"
              :amount="orderCreationForm.amount"
              :currency="merchantCountryData.currency"
            />
          </div>
        </div>

        <span slot="footer">
          <el-button
            v-if="linkExpired"
            class="create-order__button-secondary create-order__button-generate"
            @click="submitQrForm('orderCreationForm')"
          >
            {{ $t('order_create_link_generate') }}
          </el-button>
          <el-button
            class="create-order__button-secondary create-order__button-generate"
            @click="handleBackToEdit"
          >
            {{ $t('create_order_drawer_back_edit') }}
          </el-button>
          <el-button
            class="create-order__dialog-button-secondary create-order__dialog-button-secondary--red create-order__button-cancel"
            @click="cancelOrderClicked"
          >
            {{ $t('order_create_wait_cancel') }}
          </el-button>
        </span>
      </div>

      <div v-if="dialogs['smsSentDialog']" class="create-order__dialog">
        <a
          v-if="!linkExpired"
          class="el-dialog__headerbtn"
          type="text"
          target="_blank"
          :href="$t('zendesk_helper_url')"
        >
          <div class="create-order__question-icon-container">
            <svg-icon icon-class="circle-question-mark" />
          </div>
        </a>
        <span slot="title">
          <div class="create-order__dialog-icon">
            <i v-if="!linkExpired" class="el-icon-loading" />
            <svg-icon v-if="linkExpired" icon-class="error" />
          </div>
          <div v-if="!linkExpired" class="create-order__dialog-title">
            {{ $t('order_create_wait_title') }}
          </div>
          <div v-if="linkExpired" class="create-order__dialog-title">
            {{ $t('order_create_link_expired_title') }}
          </div>
        </span>
        <div class="create-order__dialog-content">
          <div v-if="!linkExpired" class="create-order__dialog-content-text">
            {{ $t('order_create_wait_description') }}
          </div>
          <div v-if="linkExpired" class="create-order__dialog-content-text">
            {{ $t('order_create_link_expired_description') }}
          </div>
          <div class="create-order__dialog-content-box">
            <div>
              <div>{{ $t('order_create_wait_phone') }}</div>
              <div>
                +{{
                  merchantCountryData.telephoneCode +
                    orderCreationForm.phoneNumber
                }}
              </div>
            </div>
            <div>
              <div>{{ $t('order_create_wait_total') }}</div>
              <Amount
                :amount="orderCreationForm.amount"
                :currency="merchantCountryData.currency"
              />
            </div>
            <div>
              <div>{{ $t('order_create_wait_link_expire') }}</div>
              <div :class="linkExpired ? 'create-order__dialog-text-red' : ''">
                {{ linkExpireTimer }}
              </div>
            </div>
          </div>
        </div>
        <span slot="footer">
          <el-button
            v-if="linkExpired"
            class="create-order__button-secondary create-order__button-generate"
            @click="submitPaymentLinkForm('orderCreationForm', true)"
          >
            {{ $t('order_create_link_generate') }}
          </el-button>
          <el-button
            class="create-order__dialog-button-secondary create-order__dialog-button-secondary--red"
            @click="cancelOrderClicked"
          >
            {{ $t('order_create_wait_cancel') }}
          </el-button>
        </span>
      </div>

      <div v-if="dialogs['loadingDialog']" class="create-order__dialog">
        <span slot="title">
          <div class="create-order__dialog-icon">
            <i class="el-icon-loading" />
          </div>
          <div class="create-order__dialog-title">
            {{ $t('order_create_loading_title') }}
          </div>
        </span>
        <div class="create-order__dialog-content">
          <div class="create-order__dialog-content-text">
            {{ $t('order_create_loading_description') }}
          </div>
        </div>
      </div>
      <div
        v-if="dialogs['cancelConfirmationDialog']"
        class="create-order__dialog"
      >
        <span slot="title">
          <div
            class="create-order__dialog-title create-order__dialog-title-confirm"
          >
            {{ $t('order_create_cancel_title') }}
          </div>
        </span>
        <div class="create-order__dialog-content">
          <div class="create-order__dialog-content-text">
            {{ $t('order_create_cancel_description') }}
          </div>
        </div>
        <span slot="footer">
          <el-button
            class=" create-order__button-secondary create-order__button-generate"
            @click="stayOnPage"
          >
            {{ $t('order_create_cancel_button_stay') }}
          </el-button>
          <el-button
            class="create-order__button-secondary create-order__button-no-border"
            @click="cancelOrderProcess"
          >
            {{ $t('order_create_cancel_button_cancel') }}
          </el-button>
        </span>
      </div>
      <div
        v-if="dialogs['successDialog']"
        :custom-class="
          'create-order__dialog create-order__dialog--flexible-width'
        "
      >
        <span slot="title">
          <div
            class="create-order__dialog-icon create-order__dialog-icon-success"
          >
            <svg-icon icon-class="success" />
          </div>
          <div
            class="create-order__dialog-title create-order__dialog-title--success"
          >
            {{ $t('create_order_dialog_title_successful') }}
          </div>
        </span>
        <div class="create-order__dialog-content">
          <div
            class="create-order__dialog-content-text create-order__dialog-content-code"
          >
            {{ $t('order_create_approval_code') }} #{{
              orderDetail.orderReferenceId
            }}
          </div>
          <div class="create-order__barcode-container">
            <VueBarcode
              v-if="barcodeValue"
              :value="barcodeValue"
              :display-value="false"
            />
          </div>
          <div class="create-order__barcode-value">
            #{{ barcodeValueWillBeDisplayed }}
          </div>
          <div class="create-order__divider create-order__divider-success" />

          <div
            class="create-order__dialog-content-box create-order__dialog-content-box-success"
          >
            <div>
              <div class="create-order__table-title">
                {{ $t('order_create_complete_date') }}
              </div>
              <div class="create-order__table-value">
                {{ orderDetail.createdAt | dateFormatter }},
                {{ orderDetail.createdAt | timeFormatter }}
              </div>
            </div>
            <div>
              <div class="create-order__table-title">
                {{ $t('order_create_wait_total') }}
              </div>
              <!--              <div> {{ `${merchantCountryData.currency} ${this.orderCreationForm.amount}` }}</div>-->
              <div class="create-order__table-value">
                <Amount
                  :amount="orderDetail.totalAmount.amount"
                  :currency="orderDetail.totalAmount.currency"
                />
              </div>
            </div>
          </div>
        </div>
        <span slot="footer">
          <el-button
            class="create-order__button-secondary create-order__button-generate"
            @click="closeLastStepDialog"
          >
            {{ $t('order_create_close') }}
          </el-button>
          <el-button
            class=" create-order__button-secondary create-order__button-no-border"
            @click="goToOrderDetails"
          >
            {{ $t('order_create_see_details') }}
          </el-button>
        </span>
      </div>
      <div
        v-if="orderDetail && dialogs['failedDialog']"
        :class="'create-order__dialog create-order__dialog--flexible-width'"
      >
        <span slot="title">
          <div
            class="create-order__dialog-icon create-order__dialog-icon-error"
          >
            <svg-icon icon-class="error" />
          </div>
          <div class="create-order__dialog-title">
            {{ $t('order_create_failed_title') }}
          </div>
        </span>
        <div class="create-order__dialog-content">
          <div class="create-order__dialog-content-text">
            {{ $t('order_create_failed_description') }}
          </div>
        </div>
        <span slot="footer">
          <el-button
            class="create-order__button-secondary create-order__button-generate"
            @click="closeLastStepDialog"
          >
            {{ $t('order_create_close') }}
          </el-button>
        </span>
      </div>
      <div
        v-if="dialogs['errorDialog']"
        :class="'create-order__dialog create-order__dialog--flexible-width'"
      >
        <span slot="title">
          <div
            class="create-order__dialog-icon create-order__dialog-icon-error"
          >
            <svg-icon icon-class="error" />
          </div>
          <div class="create-order__dialog-title">
            {{ error.title }}
          </div>
        </span>
        <div class="create-order__dialog-content">
          <div class="create-order__dialog-content-text">
            {{ $te(error.message) ? $t(error.message) : error.message }}
          </div>
        </div>
        <span slot="footer">
          <el-button
            class="create-order__button-secondary create-order__button-generate"
            @click="handleBackToEdit"
          >
            {{ $t('order_create_close') }}
          </el-button>
        </span>
      </div>
    </el-drawer>
  </div>
</template>

<style lang="scss" scoped>
.create-order__dialog {
  text-align: center;
}

.create-order__dialog .create-order__dialog-content-text {
  margin-top: 16px;
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 28px;
  color: #666;
}

.create-order__dialog-content-code {
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #666;
  text-align: center;
}

.create-order ::v-deep .create-order-drawer.el-drawer {
  width: 450px !important;

  [dir='ltr'] & {
    padding: 40px 0 20px 30px;
    text-align: left;
  }

  [dir='rtl'] & {
    padding: 40px 30px 20px 0;
    text-align: right;
  }
}

.create-order ::v-deep .vue-barcode-element {
  margin: auto;
}

.create-order ::v-deep .create-order__barcode-value {
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #666;
  text-align: center;
}

.create-order ::v-deep .el-input__inner:focus {
  border-color: #000;
  -webkit-transition: 0.3s all ease-in-out;
  transition: 0.3s all ease-in-out;
}
.create-order ::v-deep .el-input__inner::placeholder {
  color: #ccc;
}

.create-order ::v-deep .create-order-drawer.el-drawer .el-drawer__body {
  overflow-y: auto;

  [dir='ltr'] & {
    padding-right: 32px;
  }

  [dir='rtl'] & {
    padding-left: 32px;
  }
}

.create-order.create-order-advance
  ::v-deep
  .create-order-drawer.el-drawer
  .el-drawer__body {
  margin-bottom: 120px;
}

.create-order__dialog-button-secondary--red {
  width: 100%;
  margin-left: 0 !important;
  color: #e02d48;
  border: none;
}

.create-order__switch ::v-deep .el-switch__core {
  width: 51px !important;
  background: #78788052;
  border-color: transparent;
}
.create-order__switch ::v-deep .el-switch__core::after {
  top: 2px;
  left: 2px;
  width: 23px;
  height: 23px;
  content: '';
  background: white;
}

.create-order__switch.is-checked ::v-deep .el-switch__core {
  background: #8267db;
  border-color: #8267db;
}

.create-order__switch.is-checked ::v-deep .el-switch__core::after {
  left: 100%;
  margin-left: -26px;
  content: '';
}

.create-order__phone-field ::v-deep .el-input-group__prepend,
.create-order__phone-field ::v-deep .el-input-group__append {
  background: transparent;
  border: none;
}

.create-order__phone-field ::v-deep .el-input__inner {
  padding-left: 0 !important;
  border: none !important;
}

.create-order__phone-field {
  border: 1.5px solid #e5e5e5;
  border-radius: 10px;
}

.create-order__phone-prefix {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #000;
  cursor: not-allowed;

  > *:not(:last-child) {
    [dir='ltr'] & {
      margin-right: 8px;
    }

    [dir='rtl'] & {
      margin-left: 8px;
    }
  }
}

.create-order__dialog__heading--qr {
  margin-top: 60px;
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
}

.create-order__phone-prefix-flag {
  width: 18px;
  height: 18px;
}

.create-order__phone-prefix-arrow {
  width: 7px;
  height: 7px;
}

.create-order__dialog__amount {
  display: block;
  margin-bottom: 60px;
  text-align: center;
  border: none;
}

.create-order__advance {
  display: flex;
  gap: 2px;
  align-items: center;
  justify-content: space-between;
  margin-top: 5px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #666;

  .el-switch {
    margin-left: 5px;
  }
}

.create-order ::v-deep .el-form .el-form-item {
  margin-bottom: 22px;
}

.create-order__question-icon-container {
  display: none;
}

.qr-wrapper {
  width: 240px;
  padding: 18px 0;
  margin: 20px auto;
  border: 1px solid #e2e2e2;
  border-radius: 17px;
}

.qr-wrapper ::v-deep .qr-code__wrap canvas {
  margin: auto;
}

.create-order
  ::v-deep
  .el-form
  .el-form-item.is-error
  .el-form-item__content
  .value-field
  .el-input__inner {
  border-color: #e83550 !important;
}

.create-order
  ::v-deep
  .el-form
  .el-form-item
  .el-form-item__content
  .el-form-item__error {
  position: relative;
  margin-top: 5px;
  color: #e02d48;
}

.create-order ::v-deep .el-form .el-form-item .el-form-item__label {
  margin-bottom: 5px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;

  [dir='rtl'] & {
    float: right;
    padding-right: 7px;
  }
}
.create-order ::v-deep .el-form .el-form-item .el-form-item__label::after {
  padding-right: 0;
}
.create-order
  ::v-deep
  .el-form
  .el-form-item
  .el-form-item__label
  > span
  > span {
  color: #999;
}

.create-order__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  font-size: 24px;
  font-weight: 600;
  line-height: 48px;
  letter-spacing: -0.02em;
}

.create-order__title span {
  cursor: pointer;
}

.create-order__btn-group {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.create-order__dialog__timer {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #666;
  text-align: center;
}

.create-order__divider {
  width: 100%;
  height: 1px;
  margin: 30px 0;
  background: #f2f2f2;
}

.create-order__divider-success {
  margin-bottom: 0;
}

.create-order__btn-group-footer {
  position: fixed;
  bottom: 0;
  display: flex;
  gap: 10px;
  width: 408px;
  padding: 24px 40px;
  background: #f7f7f7;

  [dir='ltr'] & {
    right: 0;
  }

  [dir='rtl'] & {
    left: 0;
  }
}

.create-order__button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 167px;
  height: 60px;
  font-size: 14px;
  background: #8267db;
  border: 1px solid #8267db;
  border-radius: 14px;

  &:active,
  &:focus {
    background: #8267db;
  }
}

.create-order__button-secondary:hover ::v-deep span svg,
.create-order__button-secondary:active ::v-deep span svg,
.create-order__button-secondary:focus ::v-deep span svg {
  filter: none !important;
}

.create-order__button.is-disabled {
  background: #0000001a;
  border: none !important;
  border-radius: 14px;
}

.create-order__button--disable ::v-deep span,
.create-order__button-secondary ::v-deep span,
.create-order__button ::v-deep span {
  display: flex;
  align-items: center;
  justify-content: center;
}

.create-order__button--disable ::v-deep svg,
.create-order__button-secondary ::v-deep svg,
.create-order__button ::v-deep svg {
  width: 20px;
  height: 20px;
  margin-inline-end: 8px;
}

.create-order__button--disable {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 167px;
  height: 60px;
  font-size: 14px;
  color: #999;
  background: #0000001a;
  border: 1px solid #0000001a;
  border-radius: 14px;
}

.create-order__button-secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 167px;
  height: 60px;
  font-size: 14px;
  color: #999;
  background: #fff;
  border: 1px solid #999;
  border-radius: 14px;

  &:active,
  &:focus {
    background: transparent;
  }
}

.create-order__button-no-border {
  width: 100%;
  margin-left: 0 !important;
  color: black;
  border: none;
}

.create-order__button-generate {
  width: 100%;
  margin-top: 10px !important;
  margin-left: 0 !important;
  color: black !important;

  &:hover {
    color: #fff !important;
  }
}

.create-order__button-cancel {
  width: 100%;
  height: 60px;
  margin-top: 10px;
  margin-left: 0 !important;
  border-radius: 14px;
}

.create-order__dialog__amount__label {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
}

.create-order__dialog__amount__total {
  margin-top: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
}

.footer-btn {
  height: 56px !important;
  border-color: #0000001a !important;
  border-radius: 50px !important;
}

.value-field ::v-deep .el-input__inner {
  min-height: 48px !important;
  color: #666;
  border: 1.5px solid #e5e5e5;
  border-radius: 10px;
}
.value-field ::v-deep .el-input--suffix .el-input__inner {
  padding-inline-start: 15px;
  padding-inline-end: 30px;
}
.value-field ::v-deep .el-date-editor.el-input--suffix .el-input__inner {
  padding-inline-start: 30px;
}
.create-order__dialog .el-dialog__headerbtn {
  position: absolute !important;

  svg {
    width: 20px;
    height: 20px;
  }
}

.create-order__dialog-icon {
  font-size: 29px;

  svg {
    width: 72px !important;
    height: 72px !important;
    margin: 0 auto;
  }
}

.create-order__dialog-icon-success {
  margin-top: 60px;
  margin-bottom: 15px;
}

.create-order__dialog-icon-error {
  margin-top: 60px;
  margin-bottom: 15px;
}

.create-order__dialog .el-dialog__header,
.create-order__dialog .el-dialog__body,
.create-order__dialog .el-dialog__footer {
  padding: 0 !important;
  margin: 0 !important;
  color: #666;
}

.create-order__dialog .create-order__dialog-title {
  display: block;
  margin-top: 15px;
  margin-bottom: 15px;
  font-size: 24px;
  font-weight: 600;
  line-height: 36px;
  color: #000;
  text-align: center;
}

.create-order__dialog-title--success {
  margin-bottom: 10px;
  font-size: 24px;
  font-weight: 600;
  line-height: 37px;
  text-align: center;
  letter-spacing: -0.02em;
}

.create-order__table-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  text-align: left;
}

.create-order__table-value {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: black;
  text-align: right;
}

.create-order__dialog .create-order__dialog-title-confirm {
  margin-top: 60px;
}

.create-order__dialog .el-dialog__footer span {
  display: flex;
  flex-direction: column;

  .el-button {
    font-size: 16px;
  }

  .el-button:first-child {
    height: 52px;
    margin-bottom: 20px;
  }
}

.create-order__dialog-button-secondary {
  &:hover,
  &:active,
  &:focus {
    background: none;
  }
}

.create-order__dialog-content-box {
  margin-bottom: 40px;
  font-size: 14px;
  color: #666;
  border: 1.5px solid #e5e5e5;
  border-radius: 12px;

  > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    margin-right: 15px;
    margin-left: 15px;
    border-bottom: 1.5px solid #e5e5e5;
  }

  > div:last-child {
    border-bottom: none;
  }
}

.create-order__dialog-content-box.create-order__dialog-content-box-success {
  padding-top: 10px;
  margin-bottom: 60px;
  border: none;

  > div {
    padding: 8px 0;
    border: none;
  }
}
</style>

<style lang="scss">
.create-order-advance__time-picker-popper {
  &.el-time-panel {
    right: 53px !important;
    left: auto !important;
    width: 323px !important;

    [dir='rtl'] & {
      right: auto !important;
      left: 53px !important;
      direction: ltr;
    }
  }
}
</style>
